import { useCallback } from 'react';

export const useNodeItemController = ({
  // Data
  activeNodes,
  selectedNodeIds,
  prefabs,

  // State
  isLiveMode,
  isSpacePressed,
  containerNode,
  stack,
  isContainerInternal,

  // Dimensions
  rootW,
  rootH,
  effectiveBreakpoint,

  // Handlers
  interactionHandlers,
  nodeOps,
  setSelectedNodeIds,
  pushContext,
  clearSelection
}) => {
  // Factory function to create controller for each node
  const createNodeController = useCallback((node) => {
    // Calculate isolation status
    const getIsolationStatus = () => {
      if (!containerNode) return "none";
      if (node.id === containerNode.id) return "target";
      if (stack.some((ctx) => ctx.id === node.id)) return "ancestor";

      // Special handling for Prefab Root when drilled down
      if (node.props?.prefabId && !node.parentId && isContainerInternal) {
        return "ancestor";
      }

      // Standard Prefab Top-Level (external container) -> 'none' (Root View)
      if (node.props?.prefabId && !node.parentId && !isContainerInternal) {
        return "none";
      }

      return "inactive";
    };

    return {
      node,
      activeNodes,
      selectedNodeIds,
      prefabs,
      isLiveMode,
      isSpacePressed,
      containerNode,
      rootW,
      rootH,
      activeBreakpoint: effectiveBreakpoint || "default",
      isolationStatus: getIsolationStatus(),
      contextPath: stack.map((c) => c.id),

      // Event handlers
      onMouseDown: (e, id, axis) =>
        interactionHandlers.handleNodeMouseDown(e, id, axis, setSelectedNodeIds),
      onDoubleClick: (e, id) =>
        nodeOps.handleNodeDoubleClick(
          e,
          id,
          isLiveMode,
          isSpacePressed,
          containerNode,
          pushContext,
          clearSelection
        ),
      onResizeMouseDown: interactionHandlers.handleResizeMouseDown,
      onAxisDragMouseDown: (e, id, axis) =>
        interactionHandlers.handleNodeMouseDown(e, id, axis, setSelectedNodeIds)
    };
  }, [
    activeNodes,
    selectedNodeIds,
    prefabs,
    isLiveMode,
    isSpacePressed,
    containerNode,
    stack,
    isContainerInternal,
    rootW,
    rootH,
    effectiveBreakpoint,
    interactionHandlers,
    nodeOps,
    setSelectedNodeIds,
    pushContext,
    clearSelection
  ]);

  // Helper for preview/ghost nodes with minimal interaction
  const createPreviewController = useCallback((node, customActiveNodes = activeNodes) => {
    return {
      node,
      activeNodes: customActiveNodes,
      selectedNodeIds: [],
      prefabs,
      isLiveMode: false,
      isSpacePressed: false,
      containerNode: null,
      rootW,
      rootH,
      activeBreakpoint: effectiveBreakpoint || "default",
      isolationStatus: "none",
      contextPath: [],
      // No-op handlers for preview
      onMouseDown: () => {},
      onDoubleClick: () => {},
      onResizeMouseDown: () => {},
      onAxisDragMouseDown: () => {}
    };
  }, [activeNodes, prefabs, rootW, rootH, effectiveBreakpoint]);

  return { createNodeController, createPreviewController };
};
